import {find, map, clone, isEmpty} from 'lodash';
import moment from 'moment';
import {Fragment, useCallback, useContext, useEffect, useMemo, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {WritingOptions} from 'xlsx';

import {userApi} from 'api';
import {
    Table, TableBody, TableRow, TableCell, EmailLink, UserInitials,
    Icon, Pagination, Button, notifier, RequiredMark, IconName, DataWrapper,
    Accordion, AccordionTitle, AccordionShortDetails, AccordionDetails, IconColor,
} from 'components';
import {APP_NAME} from 'constants/app';
import {UserContext} from 'context';
import {TaskStatus, UserActionType} from 'enums';
import {useApiData, usePrevious} from 'hooks';
import {TRANSLATION_KEYS} from 'locale';
import {DataRequest} from 'services';
import {CursorPaginatedDBResponse, PaginationOptions, User, UserAudit, UserAuditFilters} from 'types';
import {dateUtils, logger} from 'utils';
import {lazyModules} from 'utils/lazy-modules';
import {formatDate, getParticipantName} from 'utils/transform';

import {UserAuditFiltersForm} from './UserAuditFilters';

const T_KEYS = TRANSLATION_KEYS.USER.ACCOUNT.USERS.USERS_AUDIT;
const ERROR_T_KEYS = TRANSLATION_KEYS.COMMON.MESSAGES.ERROR.UNEXPECTED;
const USER_AUDIT_LIMIT = 1000;

const defaultPagination: PaginationOptions = {
    page: 1,
    pages: 1,
    count: 0,
    pageSize: 10
};

type AuditCardProps = {
    iconName: IconName,
    iconColor?: IconColor
    children: React.ReactNode,
}

const AuditCardItem = ({iconName, iconColor, children}: AuditCardProps) => (
    <div className='tw-mt-2 tw-flex tw-items-center'>
        <Icon name={iconName} color={iconColor} />
        <div className='tw-flex-1 tw-break-words tw-overflow-hidden tw-ml-4'>
            {children}
        </div>
    </div>
);

type Props = {
    users: User[];
}

export const UsersAudit: React.FC<Props> = ({users = []}) => {
    const {t} = useTranslation();
    const {settings: {common: {timeZone, dateFormat, timeFormat}}} = useContext(UserContext);

    const now = useMemo(() => moment.tz(timeZone), [timeZone]);

    const defaultAuditFilters: UserAuditFilters = useMemo(() => ({
        createdAfterDate: now.clone().subtract(14, 'd'),
        createdBeforeDate: now,
        actions: [],
        authorEmail: null,
        userEmail: null
    }), [now]);

    const [appliedFilters, setAppliedFilters] = useState(clone(defaultAuditFilters));
    const [pagination, setPagination] = useState<PaginationOptions>(defaultPagination);
    const [loadingExportData, setLoadingExportData] = useState(false);
    const [loadingExportProgress, setLoadingExportProgress] = useState(0);
    const prevExportLoading = usePrevious(loadingExportData);
    const [itemsForExport, setItemsForExport] = useState([]);

    const {
        send: fetchUserAudit,
        data: {items: userAudit, count: totalCount} = {items: [], count: 0},
        loading
    } = useApiData(userApi.getUserAudit);

    const isUserAuditEmpty = useMemo(() => isEmpty(userAudit), [userAudit]);

    const headers = useMemo(() => [
        {children: t(T_KEYS.TABLE.COLUMNS.USER)},
        {children: ''},
        {children: t(T_KEYS.TABLE.COLUMNS.ACTION)},
        {children: t(T_KEYS.TABLE.COLUMNS.AUTHOR)},
        {children: ''},
        {children: t(T_KEYS.TABLE.COLUMNS.DATE)}
    ], [t]);

    const dataRequest = useMemo(() => new DataRequest<CursorPaginatedDBResponse<UserAudit>>({
        mergeResults: (acc, current) => [...(acc || []), ...current.results],
        formatResponse: (data) => ({
            status: !data.hasNext ? TaskStatus.Completed : TaskStatus.InProgress,
            progress: Math.round(loadingExportProgress + data.results.length / totalCount * 100),
            data
        }),
        onRequest: (data) => userApi.getUserAuditChunkData(
            appliedFilters,
            {next: data?.next, limit: USER_AUDIT_LIMIT}
        ),
        onProgress: setLoadingExportProgress,
        onSuccess: setItemsForExport,
        onError: () => {
            notifier.error({title: t(ERROR_T_KEYS)});
        },
        onComplete: () => {
            setLoadingExportData(false);
            setLoadingExportProgress(0);
        }
    }), [loadingExportProgress, totalCount, appliedFilters, t]);

    const renderAction = useCallback((action: UserActionType) => {
        switch (action) {
            case UserActionType.Create:
                return <div className='md:tw-ts-base-bold'>{t(T_KEYS.ACTIONS.CREATE)}</div>;
            case UserActionType.Update:
                return <div>{t(T_KEYS.ACTIONS.UPDATE)}</div>;
            case UserActionType.Delete:
                return <div className='md:tw-text-alert'>{t(T_KEYS.ACTIONS.DELETE)}</div>;
        }
    }, [t]);

    const updatePagination = useCallback((updates: Partial<PaginationOptions>) => {
        setPagination((prev) => ({...prev, ...updates}));
    }, []);

    const setPage = useCallback((value: number) => {
        updatePagination({page: value});
    }, [updatePagination]);

    const setPageSize = useCallback((value: number) => {
        updatePagination({pageSize: value});
    }, [updatePagination]);

    const handleExportAll = useCallback(() => {
        setLoadingExportData(true);
        setLoadingExportProgress(0);
        dataRequest.execute();
    }, [dataRequest]);

    const handleFiltersChange = useCallback((filters: UserAuditFilters) => {
        setAppliedFilters(filters);
        setPage(1);
    }, [setPage]);

    const templateHeaderNames = useMemo(() => [
        t(T_KEYS.TABLE.COLUMNS.USER),
        t(T_KEYS.TABLE.COLUMNS.USER_EMAIL),
        t(T_KEYS.TABLE.COLUMNS.ACTION),
        t(T_KEYS.TABLE.COLUMNS.AUTHOR),
        t(T_KEYS.TABLE.COLUMNS.AUTHOR_EMAIL),
        t(T_KEYS.TABLE.COLUMNS.DATE)
    ], [t]);

    const getUserInfo = useCallback((email: string) => {
        const user = find(users, {email});
        return ({
            user,
            label: user ? getParticipantName(user) : t(T_KEYS.DELETED_USER)
        });
    }, [t, users]);

    const formatAuditDate = useCallback((date: Date) => formatDate(
        date, timeZone, dateUtils.getDateTimeFormat(dateFormat, timeFormat)
    ), [dateFormat, timeFormat, timeZone]);

    const renderUserInfo = useCallback((email: string) => {
        const {user, label} = getUserInfo(email);
        return (
            <div className='tw-flex tw-items-center tw-text-break-word'>
                <UserInitials user={user} showUnactivated className='tw-mr-2' />
                {label}
            </div>
        );
    }, [getUserInfo]);

    const getActionIconName = useCallback((action: UserActionType) => {
        switch (action) {
            case UserActionType.Create:
                return 'user-add';
            case UserActionType.Update:
                return 'edit';
            case UserActionType.Delete:
                return 'delete';
        }
    }, []);

    const renderCardInfo = useCallback((audit: UserAudit, isShort: boolean) => (
        <div className='tw-pl-8'>
            {!isShort &&
                <AuditCardItem iconName='mail'>
                    <EmailLink email={audit.userEmail} />
                </AuditCardItem>}
            <AuditCardItem
                iconName={getActionIconName(audit.action)}
                iconColor={audit.action === UserActionType.Delete ? 'alert' : 'default'}
            >
                {renderAction(audit.action)}
            </AuditCardItem>
            <AuditCardItem iconName='agent'>
                {getUserInfo(audit.authorEmail).label}
            </AuditCardItem>
            {!isShort &&
                <Fragment>
                    <AuditCardItem iconName='mail'>
                        <EmailLink email={audit.authorEmail} />
                    </AuditCardItem>
                    <AuditCardItem iconName='calendar'>
                        {formatAuditDate(audit.date)}
                    </AuditCardItem>
                </Fragment>}
        </div>
    ), [getActionIconName, renderAction, getUserInfo, formatAuditDate]);

    const exportData = useCallback(async (sheetName: string, fileName: string, data: UserAudit[] = []) => {
        const xlsx = await lazyModules.xlsx();
        if (!xlsx) {
            return;
        }

        const wb = xlsx.utils.book_new();

        const writeOpts: WritingOptions = {bookType: 'xlsx', bookSST: true, type: 'binary', sheet: sheetName};

        try {
            const json = data.map((userAuditItem) => ({
                [t(T_KEYS.TABLE.COLUMNS.USER)]: getUserInfo(userAuditItem.userEmail).label,
                [t(T_KEYS.TABLE.COLUMNS.USER_EMAIL)]: userAuditItem.userEmail,
                [t(T_KEYS.TABLE.COLUMNS.ACTION)]: t(T_KEYS.ACTIONS[userAuditItem.action.toUpperCase()]),
                [t(T_KEYS.TABLE.COLUMNS.AUTHOR)]: getUserInfo(userAuditItem.authorEmail).label,
                [t(T_KEYS.TABLE.COLUMNS.AUTHOR_EMAIL)]: userAuditItem.authorEmail,
                [t(T_KEYS.TABLE.COLUMNS.DATE)]: formatAuditDate(userAuditItem.date),
            }));
            const ws = xlsx.utils.json_to_sheet(json, {header: templateHeaderNames});
            wb.SheetNames.push(sheetName);
            wb.Sheets[sheetName] = ws;
            wb.Props = {Author: APP_NAME};

            xlsx.writeFile(wb, fileName, writeOpts);
        } catch (error: any) {
            logger.error(error);
        }
    }, [getUserInfo, t, templateHeaderNames, formatAuditDate]);

    const refetchUsers = useCallback(() => {
        const _filters = {
            ...appliedFilters,
            createdAfterDate: moment.tz(appliedFilters.createdAfterDate, timeZone).startOf('day'),
            createdBeforeDate: moment.tz(appliedFilters.createdBeforeDate, timeZone).endOf('day'),
        };
        fetchUserAudit(_filters, pagination);
    }, [appliedFilters, timeZone, fetchUserAudit, pagination]);

    useEffect(() => {
        refetchUsers();
    }, [refetchUsers]);

    useEffect(() => {
        if (!loadingExportData && prevExportLoading && !isEmpty(itemsForExport)) {
            exportData(
                t(T_KEYS.EXPORT.SHEET_NAME),
                `${t(T_KEYS.EXPORT.FILENAME)}.xlsx`,
                itemsForExport
            );
        }
    }, [loadingExportData, prevExportLoading, itemsForExport, exportData, t]);

    useEffect(() => () => {
        dataRequest.cancel();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Fragment>
            <div className='tw-mb-4'>
                <RequiredMark />
                {` ${t(T_KEYS.DESCRIPTION)}`}
            </div>
            <div className='tw-flex -md:tw-flex-col tw-justify-end tw-items-start md:tw-items-center tw-font-medium tw-mb-4'>
                {!loading &&
                    <div className='tw-mr-auto -md:tw-order-2 -md:tw-mt-3'>
                        {`${totalCount} ${t(T_KEYS.COUNT_TITLE)}`}
                    </div>}
                <UserAuditFiltersForm
                    users={users}
                    defaultFilters={defaultAuditFilters}
                    appliedFilters={appliedFilters}
                    onFiltersChange={handleFiltersChange}
                />
                <Button
                    icon='arrow-down'
                    loading={loading}
                    width='small'
                    onClick={handleExportAll}
                >
                    {t(T_KEYS.BUTTONS.EXPORT)}
                </Button>
            </div>
            {!isUserAuditEmpty &&
                <Pagination
                    maxItemsToDisplay={9}
                    className='tw-ml-auto tw-flex-1'
                    itemsClassName='tw-min-w-64 tw-justify-between sm:tw-justify-end'
                    rangeClassName='-sm:tw-hidden'
                    pageSelectorClassName='-sm:tw-hidden'
                    pageSize={pagination.pageSize}
                    pageNumber={pagination.page}
                    totalItems={totalCount}
                    onPageNumberChange={setPage}
                    onPageSizeChange={setPageSize}
                />}
            <Table
                headers={headers}
                noData={isUserAuditEmpty}
                className='-md:tw-hidden'
                loading={loading}
            >
                <TableBody>
                    {map(userAudit, (audit: UserAudit) => (
                        <TableRow key={audit.id}>
                            <TableCell>
                                {renderUserInfo(audit.userEmail)}
                            </TableCell>
                            <TableCell><EmailLink email={audit.userEmail} /></TableCell>
                            <TableCell>{renderAction(audit.action)}</TableCell>
                            <TableCell>
                                {renderUserInfo(audit.authorEmail)}
                            </TableCell>
                            <TableCell><EmailLink email={audit.authorEmail} /></TableCell>
                            <TableCell>
                                {formatAuditDate(audit.date)}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
            <div className='md:tw-hidden'>
                <DataWrapper isLoading={loading} noData={isUserAuditEmpty}>
                    {map(userAudit, (audit: UserAudit) => (
                        <Accordion key={audit.id} className='tw-mb-2'>
                            <AccordionTitle>
                                {renderUserInfo(audit.userEmail)}
                            </AccordionTitle>
                            <AccordionShortDetails>
                                {renderCardInfo(audit, true)}
                            </AccordionShortDetails>
                            <AccordionDetails>
                                {renderCardInfo(audit, false)}
                            </AccordionDetails>
                        </Accordion>
                    ))}
                </DataWrapper>
            </div>
        </Fragment>
    );
};
